import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { last, initial, toPlainObject } from 'lodash';

import { PATH } from '../../../constants/path';
import HeaderComponent from './HeaderComponent';
import { HeadComponent } from '../../HeadComponent';
import { isNormalInteger } from '../../../utils/index';
import { withTemplate } from '../../template/withTemplate';
import HowItWorksComponent from '../../HowItWorksComponent';
import DiscountsListComponent from './DiscountsListComponent';
import CouponSectionComponent from './CouponSectionComponent';
import { getI18nByKey } from '../../../services/i18n/selector';
import { getPartner } from '../../../services/partner/selector';
import { PrimaryTemplate } from '../../template/PrimaryTemplate';
import { FICHE_MARCHAND } from '../../../services/mea/constants';
import { fetchMeasIfNeeded } from '../../../services/mea/actions';
import MeaMerchantCardComponent from './MeaMerchantCardComponent';
import { PATH_TO_PARTNER } from '../../../services/i18n/constants';
import { getMeasListByTypeId } from '../../../services/mea/selector';
import { withLoadingCondition } from '../../loading/withLoadingCondition';
import { convertHtmlToReactDomWithParams } from '../../../utils/htmlUtils';
import { displayableCashbackComplete } from '../../../services/partner/utils';
import { fetchPartnerCouponsIfNeeded } from '../../../services/coupon/actions';
import LocalizedBreadcrumbItem from '../../breadcrumb/LocalizedBreadcrumbsItem';
import { fetchPartnerDetailsIfNeeded } from '../../../services/partner/actions';
import {
    fetchPartnerDiscountsIfNeeded,
    forceFetchDiscounts
} from '../../../services/discount/actions';

import './MerchantCardPage.scss';

const getPartnerIdFromQuery = query => last(query.split('-'));
const getPartnerNameFromQuery = query => initial(query.split('-')).join('-');

export class MerchantCardPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            partnerId: -1,
            partnerName: ''
        };
    }

    componentWillMount() {
        this.initPage(
            getPartnerIdFromQuery(this.props.match.params.query),
            getPartnerNameFromQuery(this.props.match.params.query)
        );
    }

    componentDidUpdate() {
        const { partner, history, fetching, fetchPartnerDetailsIfNeeded } = this.props;
        if (!fetching && !partner) {
            history.push(PATH.PAGE_NOT_FOUND_PATH);
        } else if (partner) {
            fetchPartnerDetailsIfNeeded(partner.id);
        }
    }

    componentWillReceiveProps = nextProps => {
        const { history, isAuthenticated, forceFetchDiscounts } = this.props;
        const { partnerId, partnerName } = this.state;
        const partnerIdUrl = getPartnerIdFromQuery(nextProps.match.params.query);
        const partnerNameUrl = getPartnerNameFromQuery(nextProps.match.params.query);
        if (!isNormalInteger(partnerIdUrl)) {
            history.push(PATH.PAGE_NOT_FOUND_PATH);
        } else if (partnerId !== partnerIdUrl && partnerName !== partnerNameUrl) {
            this.initPage(partnerIdUrl, partnerNameUrl);
        }

        if (isAuthenticated !== nextProps.isAuthenticated) {
            forceFetchDiscounts(partnerId);
        }
    };

    initPage(partnerId, partnerName) {
        const {
            fetchMeasIfNeeded,
            fetchPartnerCouponsIfNeeded,
            fetchPartnerDiscountsIfNeeded
        } = this.props;
        this.setState({ partnerId, partnerName });
        fetchMeasIfNeeded(FICHE_MARCHAND);
        fetchPartnerCouponsIfNeeded(partnerId);
        fetchPartnerDiscountsIfNeeded(partnerId);
    }

    renderPage = () => {
        const {
            i18nPage,
            merchantCardMea,
            partner,
            CASHBACK,
            partnerDisplayableCashbackComplete
        } = this.props;

        return (
            <div className="flex-container-v">
                <main className="conteneur conteneur-mobile" id="p-fm">
                    <HeadComponent
                        title={format(
                            i18nPage.TITLE,
                            partner.name,
                            partnerDisplayableCashbackComplete
                        )}
                        description={i18nPage.DESCRIPTION}
                    />
                    <LocalizedBreadcrumbItem to={PATH.CASHBACK_PATH}>
                        {i18nPage.BREADCRUMB}
                    </LocalizedBreadcrumbItem>
                    <LocalizedBreadcrumbItem
                        to={`${PATH.MERCHANTPAGE_PATH}${this.state.partnerId}-${this.state.partnerName}`}>
                        {partner.name}
                    </LocalizedBreadcrumbItem>
                    <h1>{`${CASHBACK} ${partner.name}`}</h1>
                    <HeaderComponent id={this.state.partnerId} />
                    <DiscountsListComponent partnerId={this.state.partnerId} />

                    <section>
                        <h3>{i18nPage.CONDITIONS_TITLE}</h3>
                        <div>
                            {partner.conditions &&
                                convertHtmlToReactDomWithParams(partner.conditions)}
                        </div>
                    </section>

                    <MeaMerchantCardComponent mea={merchantCardMea} partner={partner} />
                    <CouponSectionComponent
                        partnerId={this.state.partnerId}
                        partnerName={this.state.partnerName}
                    />
                    {partner.description && (
                        <section className="description">
                            <h3>{`${i18nPage.ABOUT_TITLE} ${partner.name}`}</h3>
                            <div>{convertHtmlToReactDomWithParams(partner.description)}</div>
                        </section>
                    )}
                </main>
                <HowItWorksComponent topShadow showTitle cssClass="conteneur conteneur-mobile" />
            </div>
        );
    };

    render() {
        const { fetching = false, partner = null } = this.props;
        return withLoadingCondition(fetching || !partner, this.renderPage);
    }
}

MerchantCardPage.propTypes = {
    i18nPage: PropTypes.object.isRequired,
    coupons: PropTypes.array,
    discounts: PropTypes.array,
    partner: PropTypes.object,
    match: PropTypes.object.isRequired,
    partnerDisplayableCashbackComplete: PropTypes.string,
    merchantCardMea: PropTypes.object,
    user: PropTypes.object
};

// Which props do we want to inject, given the global state?
const mapStateToProps = (state, ownProps) => {
    const partnerId = getPartnerIdFromQuery(ownProps.match.params.query);
    const partner = getPartner(state, partnerId);
    const merchantCardMeas = getMeasListByTypeId(state, FICHE_MARCHAND).filter(
        mea => mea.idPartenaire === Number(partnerId)
    );
    return {
        CASHBACK: state.i18n.common.CASHBACK,
        i18nPage: state.i18n.page.MERCHANTCARD,
        isAuthenticated: state.authentication.isAuthenticated,
        user: toPlainObject(state.account.user),
        merchantCardMea: merchantCardMeas[0] || null,
        partner,
        partnerDisplayableCashbackComplete: displayableCashbackComplete(
            partner,
            getI18nByKey(state, PATH_TO_PARTNER)
        ),
        fetching: state.partner.fetching
    };
};

const mapDispatchToProps = {
    fetchMeasIfNeeded,
    fetchPartnerCouponsIfNeeded,
    fetchPartnerDiscountsIfNeeded,
    forceFetchDiscounts,
    fetchPartnerDetailsIfNeeded
};

export default withTemplate(
    connect(mapStateToProps, mapDispatchToProps)(MerchantCardPage),
    PrimaryTemplate
);



// WEBPACK FOOTER //
// ./src/components/pages/merchantcard/MerchantCardPage.js